import { useInfiniteQuery } from '@tanstack/react-query';
import { getItems, ItemSearchParams, ItemsResponse } from '../api/items';
import { useLanguageContext } from '../contexts/LanguageContext';

/**
 * Custom hook for item search
 * Uses TanStack Query for data fetching, caching, and pagination
 */
export const useItems = (searchParams: ItemSearchParams = {}) => {
  const { language } = useLanguageContext();
  
  // Use infinite query for pagination
  const query = useInfiniteQuery({
    queryKey: ['items', searchParams, language],
    queryFn: ({ pageParam = 1 }) => 
      getItems({ ...searchParams, page: pageParam, size: 100, language }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // Calculate if there are more pages based on total count
      const loadedItemsCount = lastPageParam * lastPage.size;
      return loadedItemsCount < lastPage.total ? lastPageParam + 1 : undefined;
    },
    staleTime: 30000, // 30 seconds
  });
  
  // Compute all items by flattening pages
  const items = query.data?.pages.flatMap(page => page.items) ?? [];
  
  // Calculate pagination info
  const totalItems = query.data?.pages[0]?.total ?? 0;
  const pageSize = query.data?.pages[0]?.size ?? 100;
  const totalPages = Math.ceil(totalItems / pageSize);
  const currentPage = query.data?.pages.length ?? 0;
  
  return {
    ...query,
    items,
    pagination: {
      totalItems,
      pageSize,
      totalPages,
      currentPage,
      loadedItems: items.length
    }
  };
}; 